import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { useTranslation } from "react-i18next"

/**
 * "Descartar alterações?" for create and edit dialogs (David, 2026-10-23).
 *
 * A guarded `DialogContent` (`guard`) asks before it closes by the overlay,
 * Esc, the X or a Cancel button marked `data-dialog-cancel`, when what the
 * person wrote differs from what was there when they started. "What was
 * there" is a snapshot of the dialog's fields taken at the person's first
 * keystroke or click inside it, so values an edit dialog loads on its own
 * never count as a change, and typing then erasing is not a change either.
 * A recorded or uploaded preview (`blob:` image, video, audio) counts as
 * content. `dirty` forces the answer when the dialog knows better.
 * Closing by the parent (`open={false}` after a successful save) is never
 * asked about. While dirty, leaving the page asks the browser's question.
 */

/** Every field's value (and every media preview), in document order. */
const snapshotOf = (root: HTMLElement | null): string => {
  if (!root) return ""
  const parts: string[] = []
  root.querySelectorAll("input, textarea, select, [contenteditable=true]").forEach((node) => {
    if (node instanceof HTMLInputElement) {
      if (node.type === "hidden" || node.type === "search" || node.dataset.guardIgnore !== undefined) return
      parts.push(node.type === "checkbox" || node.type === "radio" ? String(node.checked) : node.type === "file" ? String(node.files?.length ?? 0) : node.value)
    } else if (node instanceof HTMLTextAreaElement || node instanceof HTMLSelectElement) {
      parts.push(node.value)
    } else {
      parts.push((node as HTMLElement).innerText.trim())
    }
  })
  root.querySelectorAll("[aria-pressed], [role=radio], [role=checkbox]").forEach((node) => {
    parts.push(node.getAttribute("aria-pressed") ?? node.getAttribute("aria-checked") ?? "")
  })
  const media = root.querySelectorAll('img[src^="blob:"], video, audio').length
  parts.push(`media:${media}`)
  return parts.join("\u0000")
}

export interface UnsavedGuard {
  /** Props for the guarded `DialogPrimitive.Content`. */
  contentProps: {
    ref: (el: HTMLDivElement | null) => void
    onEscapeKeyDown: (e: KeyboardEvent) => void
    onPointerDownOutside: (e: Event) => void
    onPointerDownCapture: (e: React.PointerEvent) => void
    onKeyDownCapture: (e: React.KeyboardEvent) => void
    onClickCapture: (e: React.MouseEvent) => void
  }
  /** The X: asks first when dirty. */
  onCloseClick: (e: React.MouseEvent) => void
  closeRef: React.RefObject<HTMLButtonElement>
  prompt: React.ReactNode
}

export const useUnsavedGuard = (enabled: boolean, forcedDirty?: boolean): UnsavedGuard => {
  const { t } = useTranslation("common")
  const root = React.useRef<HTMLDivElement | null>(null)
  const baseline = React.useRef<string | null>(null)
  const closeRef = React.useRef<HTMLButtonElement>(null)
  const bypass = React.useRef(false)
  const pending = React.useRef<(() => void) | null>(null)
  const [asking, setAsking] = React.useState(false)
  const keepRef = React.useRef<HTMLButtonElement>(null)

  const isDirty = React.useCallback((): boolean => {
    /* A closed dialog has nothing to lose: its content (and the hook's owner) can outlive it. */
    if (!enabled || !root.current) return false
    if (forcedDirty !== undefined) return forcedDirty
    if (baseline.current === null) return false
    return snapshotOf(root.current) !== baseline.current
  }, [enabled, forcedDirty])

  const markStart = () => {
    if (enabled && baseline.current === null) baseline.current = snapshotOf(root.current)
  }

  const ask = (then: () => void) => {
    pending.current = then
    setAsking(true)
  }

  /* Leaving the page with something written: the browser's own question. */
  React.useEffect(() => {
    if (!enabled) return
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!isDirty()) return
      e.preventDefault()
      e.returnValue = ""
    }
    window.addEventListener("beforeunload", onBeforeUnload)
    return () => window.removeEventListener("beforeunload", onBeforeUnload)
  }, [enabled, isDirty])

  const closeNow = () => {
    bypass.current = true
    closeRef.current?.click()
    bypass.current = false
  }

  const contentProps: UnsavedGuard["contentProps"] = {
    /*
     * Closed: the next opening starts from a fresh snapshot. The ref callback is
     * new on every render, so React also detaches it (null) and reattaches it in
     * the same commit; only a detach still in place afterwards is a real close.
     */
    ref: (el) => {
      root.current = el
      if (!el) queueMicrotask(() => { if (!root.current) baseline.current = null })
    },
    onEscapeKeyDown: (e) => {
      if (bypass.current || !isDirty()) return
      e.preventDefault()
      ask(closeNow)
    },
    onPointerDownOutside: (e) => {
      if (bypass.current || !isDirty()) return
      e.preventDefault()
      ask(closeNow)
    },
    onPointerDownCapture: () => markStart(),
    onKeyDownCapture: (e) => { if (e.key !== "Escape" && e.key !== "Tab") markStart() },
    /* A Cancel button marked `data-dialog-cancel`: asked about, then clicked again for real. */
    onClickCapture: (e) => {
      const button = (e.target as HTMLElement).closest("[data-dialog-cancel]") as HTMLElement | null
      if (!button || bypass.current || !isDirty()) return
      e.preventDefault()
      e.stopPropagation()
      ask(() => {
        bypass.current = true
        button.click()
        bypass.current = false
      })
    }
  }

  const onCloseClick = (e: React.MouseEvent) => {
    if (bypass.current || !isDirty()) return
    e.preventDefault()
    ask(closeNow)
  }

  const prompt = enabled ? (
    <DialogPrimitive.Root open={asking} onOpenChange={(next) => { if (!next) setAsking(false) }}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-[60] bg-black/40" />
        <DialogPrimitive.Content
          role="alertdialog"
          data-testid="unsaved-guard"
          onOpenAutoFocus={(e) => { e.preventDefault(); keepRef.current?.focus() }}
          className="fixed left-1/2 top-1/2 z-[60] w-[calc(100%-1.5rem)] max-w-sm -translate-x-1/2 -translate-y-1/2 rounded-lg border bg-white p-5 shadow-xl"
        >
          <DialogPrimitive.Title className="text-base font-semibold text-gray-900">{t("unsavedGuard.title")}</DialogPrimitive.Title>
          <DialogPrimitive.Description className="mt-1.5 text-sm text-gray-600">{t("unsavedGuard.description")}</DialogPrimitive.Description>
          <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <button type="button" ref={keepRef} data-testid="unsaved-guard-keep" onClick={() => setAsking(false)}
              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-200">
              {t("unsavedGuard.keep")}
            </button>
            <button type="button" data-testid="unsaved-guard-discard"
              onClick={() => {
                setAsking(false)
                baseline.current = null
                const then = pending.current
                pending.current = null
                /* After the prompt closes, so its own focus trap is gone. */
                window.setTimeout(() => then?.(), 0)
              }}
              className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-300">
              {t("unsavedGuard.discard")}
            </button>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  ) : null

  return { contentProps, onCloseClick, closeRef, prompt }
}
